document.documentElement.classList.remove('no-js');

(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const qs = (s, ctx = document) => ctx.querySelector(s);
  const qsa = (s, ctx = document) => [...ctx.querySelectorAll(s)];

  /* QX 2.7.0 — shared safety/performance helpers */
  const qxSafeStorageRead = (key, validator = () => true, fallback = null) => {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return fallback;
      const parsed = JSON.parse(raw);
      if (!validator(parsed)) {
        localStorage.removeItem(key);
        return fallback;
      }
      return parsed;
    } catch (_) {
      try { localStorage.removeItem(key); } catch (__){}
      return fallback;
    }
  };

  const qxSafeStorageWrite = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); return true; }
    catch (_) { return false; }
  };

  const qxSafeGet = async (url, { signal, timeout = 6500, retry = 1, headers = {} } = {}) => {
    if (navigator.onLine === false) throw new Error('offline');
    let attempt = 0;
    while (attempt <= retry) {
      const timeoutController = new AbortController();
      const timer = setTimeout(() => timeoutController.abort('timeout'), timeout);
      const relay = () => timeoutController.abort('aborted');
      signal?.addEventListener?.('abort', relay, { once: true });
      try {
        const response = await fetch(url, { signal: timeoutController.signal, headers: { Accept: 'text/html', ...headers } });
        if (!response.ok) throw new Error(`http-${response.status}`);
        return response;
      } catch (error) {
        if (signal?.aborted) throw new DOMException('Aborted','AbortError');
        if (attempt >= retry) throw error;
        attempt += 1;
      } finally {
        clearTimeout(timer);
        signal?.removeEventListener?.('abort', relay);
      }
    }
  };

  const qxProductHtmlCache = new Map();
  const qxProductCacheTtl = 30000;
  const qxGetProductHtml = async (url, options = {}) => {
    const key = String(url);
    const now = Date.now();
    const hit = qxProductHtmlCache.get(key);
    if (hit && now - hit.time < qxProductCacheTtl) return hit.html;
    const response = await qxSafeGet(key, { ...options, retry: options.retry ?? 1 });
    const html = await response.text();
    qxProductHtmlCache.set(key,{html,time:now});
    if (qxProductHtmlCache.size > 12) qxProductHtmlCache.delete(qxProductHtmlCache.keys().next().value);
    return html;
  };

  const qxResolveProductVariant = async (productUrl, optionValues = [], options = {}) => {
    const url = new URL(productUrl, location.origin);
    url.searchParams.delete('variant');
    if (optionValues.length) url.searchParams.set('option_values', optionValues.join(','));
    const html = await qxGetProductHtml(url.toString(), options);
    const doc = new DOMParser().parseFromString(html,'text/html');
    const productRoot = qs('[data-product-root]',doc);
    if (!productRoot) throw new Error('product-unavailable');
    const id = qs('[data-variant-id]',productRoot)?.value || '';
    const title = qs('h1',productRoot)?.textContent?.trim() || '';
    const price = qs('[data-product-price]',productRoot)?.textContent?.trim() || '';
    const compare = qs('[data-product-compare]',productRoot)?.textContent?.trim() || '';
    const add = qs('[name="add"],[data-product-submit],button[type="submit"]',productRoot);
    const media = qs('[data-product-media-track] img,.qx-pdp__media img,img',productRoot)?.getAttribute('src') || '';
    return {id,title,price,compare,available:Boolean(id)&&!add?.disabled,media,doc,productRoot};
  };

  const qxCommerceAnnounce = message => {
    const region = qs('[data-qx-commerce-live]');
    if (!region || !message) return;
    region.textContent = '';
    requestAnimationFrame(() => { region.textContent = message; });
  };


  let qxSliderRuntimePromise=null;
  const initSliders=()=>{
    if(window.QXSliders?.boot){
      window.QXSliders.boot(document);
      return;
    }
    if(!document.querySelector('[data-qx-slider],[data-qx-scroll]')) return;
    if(qxSliderRuntimePromise) return;
    const src=document.documentElement.dataset.qxSliderRuntimeSrc;
    if(!src) return;
    qxSliderRuntimePromise=new Promise((resolve,reject)=>{
      const script=document.createElement('script');
      script.src=src;
      script.defer=true;
      script.dataset.qxSliderRuntimeLoader='';
      script.addEventListener('load',()=>{
        window.QXSliders?.boot?.(document);
        resolve(window.QXSliders||null);
      },{once:true});
      script.addEventListener('error',()=>reject(new Error('slider-runtime-load-failed')),{once:true});
      document.head.appendChild(script);
    }).catch(()=>null);
  };

  /* Header, navigation, search, and dialog focus live in qx-header-runtime.js. */
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');
  const qxHeaderRuntime=()=>window.QXHeaderShell||{};
  const activateDialog=(...args)=>qxHeaderRuntime().activateDialog?.(...args);
  const deactivateDialog=(...args)=>qxHeaderRuntime().deactivateDialog?.(...args);
  const trapDialogFocus=(...args)=>qxHeaderRuntime().trapDialogFocus?.(...args);
  const openMenu=(...args)=>qxHeaderRuntime().openMenu?.(...args);
  const closeMenu=(...args)=>qxHeaderRuntime().closeMenu?.(...args);
  const initHeader=(...args)=>qxHeaderRuntime().initHeader?.(...args);
  const openSearch=(...args)=>qxHeaderRuntime().openSearch?.(...args);
  const closeSearch=(...args)=>qxHeaderRuntime().closeSearch?.(...args);
  const initPredictiveSearch=(...args)=>qxHeaderRuntime().initPredictiveSearch?.(...args);
  const initSearchHistoryDrawer=(...args)=>qxHeaderRuntime().initSearchHistoryDrawer?.(...args);
  const initVoiceSearch=(...args)=>qxHeaderRuntime().initVoiceSearch?.(...args);
  const speakSearch=(...args)=>qxHeaderRuntime().speakSearch?.(...args);

  /* Collection filter drawer lives in qx-collection-filters.js. */
  const qxCollectionFilters=()=>window.QXCollectionFilters||{};
  const openFilters=(...args)=>qxCollectionFilters().openFilters?.(...args);
  const closeFilters=(...args)=>qxCollectionFilters().closeFilters?.(...args);

  /* QX 2.1.0–2.1.4 — UX + Conversion Intelligence */
  const ensureUxToast = () => {
    if (document.documentElement.dataset.qxUxToasts === 'false') return null;
    let toast = qs('[data-qx-ux-toast]');
    if (toast) return toast;
    toast = document.createElement('div');
    toast.className = 'qx-ux-toast';
    toast.dataset.qxUxToast = '';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    toast.setAttribute('aria-atomic', 'true');
    document.body.appendChild(toast);
    return toast;
  };

  let qxToastTimer;
  const showUxToast = message => {
    const toast = ensureUxToast();
    if (!toast || !message) return;
    clearTimeout(qxToastTimer);
    toast.textContent = message;
    qxCommerceAnnounce(message);
    toast.classList.add('is-visible');
    qxToastTimer = setTimeout(() => toast.classList.remove('is-visible'), 1800);
  };

  /* Optional commerce extras are implemented in qx-commerce-extras.js. */
  const qxExtras=()=>window.QXCommerce||{};
  const QX_WISHLIST_KEY='qxWishlistProducts';
  const qxReadArray=key=>qxExtras().qxReadArray?.(key)??[];
  const qxWriteArray=(key,items,eventName)=>qxExtras().qxWriteArray?.(key,items,eventName);
  const renderWishlistDrawer=(...args)=>qxExtras().renderWishlistDrawer?.(...args);
  const openWishlistDrawer=(...args)=>qxExtras().openWishlistDrawer?.(...args);
  const closeWishlistDrawer=(...args)=>qxExtras().closeWishlistDrawer?.(...args);
  const syncWishlistButtons=(...args)=>qxExtras().syncWishlistButtons?.(...args);
  const toggleWishlist=(...args)=>qxExtras().toggleWishlist?.(...args);
  const toggleCompare=(...args)=>qxExtras().toggleCompare?.(...args);
  const initPersonalization=(...args)=>qxExtras().initPersonalization?.(...args);
  const openCartProductQuickView=(...args)=>qxExtras().openCartProductQuickView?.(...args);
  const openCartVariantChooser=(...args)=>qxExtras().openCartVariantChooser?.(...args);
  const openWishlistVariantChooser=(...args)=>qxExtras().openWishlistVariantChooser?.(...args);
  const initQx240=(...args)=>qxExtras().initQx240?.(...args);

  /* Cart shell implementation lives in qx-cart-shell.js. */
  const qxCartShell=()=>window.QXCartShell||{};
  const openCart=(...args)=>qxCartShell().openCart?.(...args);
  const closeCart=(...args)=>qxCartShell().closeCart?.(...args);
  const syncCartCount=(...args)=>qxCartShell().syncCartCount?.(...args);
  const cartRequest=(...args)=>qxCartShell().cartRequest?.(...args);
  const addVariant=(...args)=>qxCartShell().addVariant?.(...args);

  let qxCountdownRuntimePromise=null;
  const initCountdowns=()=>{
    if(window.QXCountdowns?.boot){
      window.QXCountdowns.boot(document);
      return;
    }
    if(!document.querySelector('[data-qx-countdown],[data-countdown-copy]')) return;
    if(qxCountdownRuntimePromise)return;
    const src=document.documentElement.dataset.qxCountdownRuntimeSrc;
    if(!src)return;
    qxCountdownRuntimePromise=new Promise((resolve,reject)=>{
      const script=document.createElement('script');
      script.src=src;
      script.defer=true;
      script.dataset.qxCountdownRuntimeLoader='';
      script.addEventListener('load',()=>{
        window.QXCountdowns?.boot?.(document);
        resolve(window.QXCountdowns||null);
      },{once:true});
      script.addEventListener('error',()=>reject(new Error('countdown-runtime-load-failed')),{once:true});
      document.head.appendChild(script);
    }).catch(()=>null);
  };

  let qxTrustRuntimePromise=null;
  const initTrustCarousels=root=>{
    if(window.QXTrustCarousel?.boot){
      window.QXTrustCarousel.boot(root||document);
      return;
    }
    if(!(root||document).querySelector?.('[data-qx-trust-carousel]') && !(root?.matches?.('[data-qx-trust-carousel]'))) return;
    if(qxTrustRuntimePromise)return;
    const src=document.documentElement.dataset.qxTrustRuntimeSrc;
    if(!src)return;
    qxTrustRuntimePromise=new Promise((resolve,reject)=>{
      const script=document.createElement('script');
      script.src=src;
      script.defer=true;
      script.dataset.qxTrustRuntimeLoader='';
      script.addEventListener('load',()=>{
        window.QXTrustCarousel?.boot?.(root||document);
        resolve(window.QXTrustCarousel||null);
      },{once:true});
      script.addEventListener('error',()=>reject(new Error('trust-runtime-load-failed')),{once:true});
      document.head.appendChild(script);
    }).catch(()=>null);
  };
  const destroyTrustCarousel=root=>window.QXTrustCarousel?.destroy?.(root);

  let qxVisualEffectsPromise=null;
  const loadVisualEffects=()=>{
    if(window.QXVisualEffects?.boot){
      window.QXVisualEffects.boot(document);
      return Promise.resolve(window.QXVisualEffects);
    }
    if(qxVisualEffectsPromise)return qxVisualEffectsPromise;
    const src=document.documentElement.dataset.qxVisualEffectsSrc;
    if(!src)return Promise.resolve(null);
    qxVisualEffectsPromise=new Promise((resolve,reject)=>{
      const script=document.createElement('script');
      script.src=src;
      script.defer=true;
      script.dataset.qxVisualEffectsLoader='';
      script.addEventListener('load',()=>{
        window.QXVisualEffects?.boot?.(document);
        resolve(window.QXVisualEffects||null);
      },{once:true});
      script.addEventListener('error',()=>reject(new Error('visual-effects-load-failed')),{once:true});
      document.head.appendChild(script);
    }).catch(()=>null);
    return qxVisualEffectsPromise;
  };
  const scheduleVisualEffects=()=>{
    const run=()=>loadVisualEffects();
    if('requestIdleCallback' in window) requestIdleCallback(run,{timeout:1400});
    else setTimeout(run,320);
  };

  window.QXTheme=Object.assign(window.QXTheme||{},{
    root,
    qs,
    qsa,
    safeStorageRead:qxSafeStorageRead,
    safeStorageWrite:qxSafeStorageWrite,
    safeGet:qxSafeGet,
    getProductHtml:qxGetProductHtml,
    showToast:showUxToast
  });

  const initAll = () => { initTrustCarousels(); initSliders(); initHeader(); initPersonalization(); initPredictiveSearch(); initVoiceSearch(); initSearchHistoryDrawer(); initQx240(); initCountdowns(); scheduleVisualEffects(); syncCartCount();  schedulePerformanceRuntime(); };

  document.addEventListener('click', async e => {
    const openMenuBtn = e.target.closest('[data-mobile-menu-open]'); if (openMenuBtn) { e.preventDefault(); openMenu(openMenuBtn); return; }
    if (e.target.closest('[data-mobile-menu-close]')) { e.preventDefault(); closeMenu(); return; }

    const filterOpen = e.target.closest('[data-filter-open]'); if (filterOpen) { e.preventDefault(); openFilters(filterOpen); return; }
    if (e.target.closest('[data-filter-close]')) { e.preventDefault(); closeFilters(); return; }

    const searchOpen = e.target.closest('[data-search-open]'); if (searchOpen) { e.preventDefault(); openSearch(searchOpen); return; }
    if (e.target.closest('[data-search-close]')) { e.preventDefault(); closeSearch(); return; }

    const cartOpen = e.target.closest('[data-cart-open]'); if (cartOpen) { e.preventDefault(); closeMenu(false); openCart(cartOpen); return; }
    if (e.target.closest('[data-cart-close]')) { e.preventDefault(); closeCart(); return; }

    const change = e.target.closest('[data-cart-change]');
    if (change) {
      e.preventDefault(); change.disabled = true;
      try { await cartRequest('cart/change.js', { line: Number(change.dataset.line), quantity: Math.max(0, Number(change.dataset.quantity)) }); openCart(change); }
      catch (err) {  change.disabled = false; }
      return;
    }
    if(e.target.closest('[data-qx-wishlist-open]')){e.preventDefault();openWishlistDrawer();return}
    const voice=e.target.closest('[data-qx-voice-search]');if(voice){e.preventDefault();const d=voice.closest('[data-search-drawer]');speakSearch(d?.querySelector('[data-predictive-search-input]'));return}
    const move=e.target.closest('[data-qx-wishlist-move]');if(move){e.preventDefault();const id=Number(move.dataset.qxWishlistMove),row=move.closest('.qx-wishlist-drawer__item'),qty=Math.max(1,Number(row?.querySelector('[data-qx-wishlist-qty-input]')?.value)||1);if(!id){showUxToast('Choose a variant first');return}move.disabled=true;move.setAttribute('aria-busy','true');fetch(root+'cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items:[{id,quantity:qty}]})}).then(r=>{if(!r.ok)throw new Error();qxWriteArray(QX_WISHLIST_KEY,qxReadArray(QX_WISHLIST_KEY).filter(i=>i.handle!==move.dataset.handle),'qx:wishlist-change');syncWishlistButtons();renderWishlistDrawer();syncCartCount();showUxToast('Moved to cart')}).catch(()=>showUxToast('Could not move item')).finally(()=>{move.disabled=false;move.removeAttribute('aria-busy')});return}
    const wishQtyMinus=e.target.closest('[data-qx-wishlist-qty-minus]'),wishQtyPlus=e.target.closest('[data-qx-wishlist-qty-plus]');
    if(wishQtyMinus||wishQtyPlus){e.preventDefault();const row=e.target.closest('.qx-wishlist-drawer__item'),input=row?.querySelector('[data-qx-wishlist-qty-input]');if(input)input.value=Math.max(1,(Number(input.value)||1)+(wishQtyPlus?1:-1));return}
    const wishVariant=e.target.closest('[data-qx-wishlist-change-variant]');
    if(wishVariant){e.preventDefault();openWishlistVariantChooser(wishVariant);return}
    const syncWish=e.target.closest('[data-qx-wishlist-sync]');if(syncWish){e.preventDefault();const url=syncWish.dataset.syncUrl,status=qs('[data-qx-wishlist-sync-status]');const setSyncState=state=>{if(!status)return;status.dataset.state=state;const map={local:'localLabel',syncing:'syncingLabel',synced:'syncedLabel',unavailable:'unavailableLabel',error:'errorLabel'};status.textContent=status.dataset[map[state]]||state};if(!url){setSyncState('unavailable');return}syncWish.disabled=true;syncWish.setAttribute('aria-busy','true');setSyncState('syncing');fetch(url,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({action:'sync',timestamp:new Date().toISOString(),items:qxReadArray(QX_WISHLIST_KEY).map(({handle,variantId})=>({handle,variantId}))})}).then(r=>{if(!r.ok)throw new Error();setSyncState('synced');showUxToast('Wishlist synced')}).catch(()=>{setSyncState('error');showUxToast('Wishlist sync service unavailable')}).finally(()=>{syncWish.disabled=false;syncWish.removeAttribute('aria-busy')});return}
    const cminus=e.target.closest('[data-qx-card-qty-minus]'),cplus=e.target.closest('[data-qx-card-qty-plus]');if(cminus||cplus){e.preventDefault();const box=e.target.closest('[data-qx-card-qty]'),inp=box?.querySelector('[data-qx-card-qty-input]');if(inp)inp.value=Math.max(1,(Number(inp.value)||1)+(cplus?1:-1));return}
    const save=e.target.closest('[data-qx-cart-save-later]');if(save){e.preventDefault();save.disabled=true;save.setAttribute('aria-busy','true');const item={handle:save.dataset.handle,title:save.dataset.title,url:save.dataset.url,image:save.dataset.image,price:save.dataset.price,vendor:save.dataset.vendor,variantId:save.dataset.variantId,quantity:Math.max(1,Number(save.dataset.lineQuantity)||1)};try{const next=[item,...qxReadArray(QX_WISHLIST_KEY).filter(i=>i.handle!==item.handle)].slice(0,50);localStorage.setItem(QX_WISHLIST_KEY,JSON.stringify(next));window.dispatchEvent(new CustomEvent('qx:wishlist-change',{detail:{items:next}}))}catch(_){showUxToast('Could not save item');save.disabled=false;save.removeAttribute('aria-busy');return}fetch(root+'cart/change.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:save.dataset.lineKey,quantity:0})}).then(r=>{if(!r.ok)throw new Error();syncWishlistButtons();renderWishlistDrawer();syncCartCount();showUxToast('Saved for later');openWishlistDrawer()}).catch(()=>showUxToast('Item was saved, but could not be removed from cart')).finally(()=>{save.disabled=false;save.removeAttribute('aria-busy')});return}
    const cqvMinus=e.target.closest('[data-qx-cart-qv-minus]'),cqvPlus=e.target.closest('[data-qx-cart-qv-plus]');
    if(cqvMinus||cqvPlus){e.preventDefault();const d=e.target.closest('[data-qx-cart-qv-dialog]'),i=qs('[data-qx-cart-qv-qty]',d);if(i)i.value=Math.max(1,Math.min(99,(Number(i.value)||1)+(cqvPlus?1:-1)));return}
    if(e.target.closest('[data-qx-cart-qv-close]')){e.preventDefault();e.target.closest('dialog')?.close();return}
    const cqva=e.target.closest('[data-qx-cart-qv-add]');if(cqva){e.preventDefault();const d=cqva.closest('[data-qx-cart-qv-dialog]'),id=Number(qs('[data-qx-cart-qv-variant]',d)?.value||0),qty=Math.max(1,Number(qs('[data-qx-cart-qv-qty]',d)?.value)||1);if(!id){showUxToast('Choose an available variant');return}cqva.disabled=true;fetch(root+'cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items:[{id,quantity:qty}]})}).then(r=>{if(!r.ok)throw new Error();d.close();openCart(cqva);showUxToast('Added to cart')}).catch(()=>showUxToast('Could not add product')).finally(()=>cqva.disabled=false);return}
    const qxVariantChange=e.target.closest('[data-qx-cart-change-variant]');if(qxVariantChange){e.preventDefault();openCartVariantChooser(qxVariantChange);return}
    const cqv=e.target.closest('[data-qx-cart-product-quick-view]');if(cqv){e.preventDefault();openCartProductQuickView(cqv);return}
    const bundle=e.target.closest('[data-qx-cart-bundle-add],[data-qx-pdp-bundle-add]');if(bundle){e.preventDefault();const wrap=bundle.closest('[data-qx-cart-bundle],[data-qx-pdp-bundle]');const items=[...wrap.querySelectorAll('input[type=checkbox]:checked')].map(x=>({id:Number(x.value),quantity:1})).filter(x=>x.id);if(!items.length){showUxToast('Select at least one product');return}bundle.disabled=true;fetch(root+'cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items})}).then(r=>{if(!r.ok)throw new Error();showUxToast('Bundle added');syncCartCount()}).catch(()=>showUxToast('Could not add bundle')).finally(()=>bundle.disabled=false);return}
    const wish=e.target.closest('[data-qx-wishlist-toggle]');if(wish){e.preventDefault();e.stopPropagation();toggleWishlist(wish);return}
    if(e.target.closest('[data-qx-wishlist-close]')){e.preventDefault();closeWishlistDrawer();return}
    const wishRemove=e.target.closest('[data-qx-wishlist-drawer-remove]');
    if(wishRemove){
      e.preventDefault();
      const next=qxReadArray(QX_WISHLIST_KEY).filter(i=>i.handle!==wishRemove.dataset.qxWishlistDrawerRemove);
      qxWriteArray(QX_WISHLIST_KEY,next,'qx:wishlist-change');
      syncWishlistButtons();
      renderWishlistDrawer();
      return;
    }
    if(e.target.closest('[data-qx-wishlist-drawer-clear]')){
      e.preventDefault();
      qxWriteArray(QX_WISHLIST_KEY,[],'qx:wishlist-change');
      syncWishlistButtons();
      renderWishlistDrawer();
      return;
    }
    const comp=e.target.closest('[data-qx-compare-toggle]');if(comp){e.preventDefault();e.stopPropagation();toggleCompare(comp);return}
    if(e.target.closest('[data-qx-cart-variant-close]')){e.preventDefault();e.target.closest('dialog')?.close();return}
    const variantSave=e.target.closest('[data-qx-cart-variant-save]');if(variantSave){e.preventDefault();const d=variantSave.closest('[data-qx-cart-variant-dialog]'),id=Number(d?.querySelector('[data-qx-cart-variant-id]')?.value||0),qty=Math.max(1,Number(d?.dataset.lineQuantity)||1);if(!id){showUxToast('Choose an available variant');return}variantSave.disabled=true;variantSave.setAttribute('aria-busy','true');let added=false;fetch(root+'cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items:[{id,quantity:qty}]})}).then(r=>{if(!r.ok)throw new Error();added=true;return fetch(root+'cart/change.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({id:d.dataset.lineKey,quantity:0})})}).then(r=>{if(!r.ok)throw new Error();d.close();openCart(variantSave);showUxToast('Variant updated')}).catch(()=>{showUxToast(added?'New variant was added, but the previous cart line could not be removed. Review your cart.':'Could not update variant')}).finally(()=>{variantSave.disabled=false;variantSave.removeAttribute('aria-busy')});return}
    const recPrev=e.target.closest('[data-qx-rec-prev]'),recNext=e.target.closest('[data-qx-rec-next]');if(recPrev||recNext){const rail=e.target.closest('.qx-cart-complementary')?.querySelector('[data-qx-rec-rail]');rail?.scrollBy({left:(recNext?1:-1)*rail.clientWidth*.75,behavior:'smooth'});return}
    if(e.target.closest('[data-qx-wishlist-variant-close]')){e.preventDefault();e.target.closest('dialog')?.close();return}
    const wishlistVariantSave=e.target.closest('[data-qx-wishlist-variant-save]');
    if(wishlistVariantSave){
      e.preventDefault();
      const d=wishlistVariantSave.closest('[data-qx-wishlist-variant-dialog]'),id=d?.querySelector('[data-qx-wishlist-variant-id]')?.value;
      if(!id){showUxToast('Choose an available variant');return}
      const items=qxReadArray(QX_WISHLIST_KEY).map(i=>i.handle===d.dataset.handle?{...i,variantId:id,variantTitle:d.dataset.variantTitle||i.variantTitle||'',available:d.dataset.variantAvailable!=='false',price:d.dataset.variantPrice||i.price}:i);
      qxWriteArray(QX_WISHLIST_KEY,items,'qx:wishlist-change');
      d.close();
      renderWishlistDrawer();
      syncWishlistButtons();
      showUxToast('Wishlist variant updated');
      return;
    }


    const quick = e.target.closest('[data-cart-add]');
    if (quick) {
      e.preventDefault(); quick.disabled = true;
      try { await addVariant(quick.dataset.variantId, 1); openCart(quick); }
      catch (err) {  quick.disabled = false; }
      return;
    }
  });

  document.addEventListener('submit', async e => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!action.includes('/cart/add') || !qs('[data-qx-cart-shell]')) return;
    e.preventDefault();
    const submit = form.querySelector('[type="submit"]');


    if (submit) {
      submit.disabled = true;
    }

    try {
      const fd = new FormData(form);
      await cartRequest('cart/add.js', fd, true);
      openCart(submit);
    } catch (err) {
      
      if (submit) {
        submit.disabled = false;
      }
    }
  });

  document.addEventListener('change', e => {
    const localization = e.target.closest('[data-localization-select]');
    if (localization) { localization.form?.submit(); return; }
    const sort = e.target.closest('[data-qx-sort]'); if (!sort) return;
    const url = new URL(location.href); url.searchParams.set('sort_by', sort.value); url.searchParams.delete('page'); location.href = url.toString();
  });

  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && qs('[data-qx-wishlist-drawer]:not([hidden])')) closeWishlistDrawer();
    trapDialogFocus(e);

    if (e.key !== 'Escape') return;

    if (qs('[data-search-drawer].is-open')) { closeSearch(); return; }
    if (qs('[data-mobile-menu].is-open')) { closeMenu(); return; }
    if (qs('[data-filter-shell].is-open')) { closeFilters(); return; }
    if (qs('[data-qx-cart-shell].is-open')) { closeCart(); return; }

  });
  document.addEventListener('shopify:section:unload', event => destroyTrustCarousel(event.target));
  document.addEventListener('shopify:section:load', initAll);
  document.addEventListener('shopify:block:select', initAll);
  document.addEventListener('DOMContentLoaded', initAll);

  let qxPerformanceRuntimePromise=null;
  const schedulePerformanceRuntime=()=>{
    const run=()=>{
      if(window.QXPerformanceRuntime?.boot){
        window.QXPerformanceRuntime.boot();
        return;
      }
      if(qxPerformanceRuntimePromise)return;
      const src=document.documentElement.dataset.qxPerformanceRuntimeSrc;
      if(!src)return;
      qxPerformanceRuntimePromise=new Promise((resolve,reject)=>{
        const script=document.createElement('script');
        script.src=src;
        script.defer=true;
        script.dataset.qxPerformanceRuntimeLoader='';
        script.addEventListener('load',()=>{
          window.QXPerformanceRuntime?.boot?.();
          resolve(window.QXPerformanceRuntime||null);
        },{once:true});
        script.addEventListener('error',()=>reject(new Error('performance-runtime-load-failed')),{once:true});
        document.head.appendChild(script);
      }).catch(()=>null);
    };
    if('requestIdleCallback' in window) requestIdleCallback(run,{timeout:1600});
    else setTimeout(run,420);
  };


})();
